import { BusinessPolicyNumberOperator } from '../../../modules/business-policies/enums/business-policy-number-operator.enum';
import { BusinessPolicySetOperator } from '../../../modules/business-policies/enums/business-policy-set-operator.enum';
import { CurrentOrderHandlingEnum } from '../../../modules/business-policies/enums/current-order-handling.enum';
import { StatusType, SynchronizedType } from '../../../modules/orders/order.types';
import { CompactAvailability } from '../../../modules/products/ProductTypes';
import { BusinessPolicyReferenceDTO } from './businessPolicies.types';

export enum BusinessPolicyNodeKind {
  LOGICAL = 'LOGICAL',
  COMPARISON = 'COMPARISON',
}

export enum BusinessPolicyAstLogicalOperator {
  AND = 'AND',
  OR = 'OR',
  NOT = 'NOT',
}

export enum BusinessPolicyFactType {
  PIECES = 'PIECES',
  GROSS_VALUE_SUM = 'GROSS_VALUE_SUM',
  UNIT_PRICE_SUM = 'UNIT_PRICE_SUM',
  NET_VALUE = 'NET_VALUE',
  CREDIT_LIMIT = 'CREDIT_LIMIT',
  AVAILABLE_BALANCE = 'AVAILABLE_BALANCE',
  VOUCHER_VALUE = 'VOUCHER_VALUE',
}

export enum BusinessPolicyGrain {
  ORDER = 'ORDER',
  PRODUCT_COLOR = 'PRODUCT_COLOR',
  ORDER_PRODUCT = 'ORDER_PRODUCT',
}

export enum BusinessPolicyMeasureSource {
  CURRENT_ORDER = 'CURRENT_ORDER',
  ORDER_HISTORY = 'ORDER_HISTORY',
  SUGGESTED_ORDER = 'SUGGESTED_ORDER',
}

export enum BusinessPolicyZeroComparison {
  PASS = 'PASS',
  FAIL = 'FAIL',
  EVALUATE = 'EVALUATE',
}

export enum BusinessPolicyUnmatchedHandling {
  LEFT = 'LEFT',
  RIGHT = 'RIGHT',
  BOTH = 'BOTH',
  NONE = 'NONE',
}

export enum BusinessPolicyScopeDimension {
  AVAILABILITY = 'AVAILABILITY',
  DELIVERY_WINDOW = 'DELIVERY_WINDOW',
  CLASSIFICATION = 'CLASSIFICATION',
  PRODUCT_COLOR = 'PRODUCT_COLOR',
  COLLECTION = 'COLLECTION',
  ORDER_CREATION_DATE = 'ORDER_CREATION_DATE',
}

interface BusinessPolicyScopeBaseDTO {
  references?: BusinessPolicyReferenceDTO[];
}

interface BusinessPolicyListScopeBaseDTO extends BusinessPolicyScopeBaseDTO {
  setOperator: BusinessPolicySetOperator;
}

export interface BusinessPolicyAvailabilityScopeDTO extends BusinessPolicyListScopeBaseDTO {
  dimension: BusinessPolicyScopeDimension.AVAILABILITY;
  availabilities: CompactAvailability[];
}

export interface BusinessPolicyDeliveryWindowScopeDTO extends BusinessPolicyListScopeBaseDTO {
  dimension: BusinessPolicyScopeDimension.DELIVERY_WINDOW;
  deliveryWindowIds: string[];
}

export interface BusinessPolicyClassificationScopeDTO extends BusinessPolicyListScopeBaseDTO {
  dimension: BusinessPolicyScopeDimension.CLASSIFICATION;
  classificationIds: string[];
}

export interface BusinessPolicyProductColorScopeDTO extends BusinessPolicyListScopeBaseDTO {
  dimension: BusinessPolicyScopeDimension.PRODUCT_COLOR;
  productColorIds: string[];
}

interface BusinessPolicyCollectionScopeDTO extends BusinessPolicyListScopeBaseDTO {
  dimension: BusinessPolicyScopeDimension.COLLECTION;
  collectionIds: string[];
}

export interface BusinessPolicyOrderCreationDateScopeDTO extends BusinessPolicyScopeBaseDTO {
  dimension: BusinessPolicyScopeDimension.ORDER_CREATION_DATE;
  startDate: string | null;
  endDate: string | null;
}

export type BusinessPolicyIdListScopeDTO =
  | BusinessPolicyDeliveryWindowScopeDTO
  | BusinessPolicyClassificationScopeDTO
  | BusinessPolicyProductColorScopeDTO
  | BusinessPolicyCollectionScopeDTO;

export type BusinessPolicyScopeDTO =
  | BusinessPolicyAvailabilityScopeDTO
  | BusinessPolicyIdListScopeDTO
  | BusinessPolicyOrderCreationDateScopeDTO;

export interface BusinessPolicyOrderHistoryDTO {
  statusSetOperator: BusinessPolicySetOperator;
  orderStatuses: StatusType[];
  synchronizedStatuses: SynchronizedType[];
  includeCustomerEconomicGroup: boolean;
  includeBrandGroup: boolean;
  currentOrderHandling: CurrentOrderHandlingEnum;
}

export interface BusinessPolicyMeasureDTO {
  fact: BusinessPolicyFactType;
  grain: BusinessPolicyGrain;
  source: BusinessPolicyMeasureSource;
  scopes: BusinessPolicyScopeDTO[];
  orderHistory: BusinessPolicyOrderHistoryDTO | null;
  priceCompositionIds: string[];
}

export interface BusinessPolicyLogicalNodeDTO {
  id?: string;
  kind: BusinessPolicyNodeKind.LOGICAL;
  operator: BusinessPolicyAstLogicalOperator;
  children: BusinessPolicyConditionNodeDTO[];
}

export interface BusinessPolicyComparisonNodeDTO {
  id?: string;
  kind: BusinessPolicyNodeKind.COMPARISON;
  comparisonOperator: BusinessPolicyNumberOperator;
  left: BusinessPolicyMeasureDTO;
  rightConstant: number | null;
  rightMeasure: BusinessPolicyMeasureDTO | null;
  rightFactor: number | null;
  zeroComparison: BusinessPolicyZeroComparison;
  includeAbsentGroups: boolean;
  unmatchedHandling: BusinessPolicyUnmatchedHandling;
}

export type BusinessPolicyConditionNodeDTO =
  | BusinessPolicyLogicalNodeDTO
  | BusinessPolicyComparisonNodeDTO;
